<template>
  <v-container fluid>
    <v-card v-if="record" variant="outlined">
      <v-card-title class="d-flex flex-wrap align-center ga-2 bg-blue-grey-lighten-4">
        <span>Finished-Goods Inventory Receipt {{ record.code }}</span>
        <v-spacer />
        <v-chip :color="approvalColor(record.approval_status)" size="small">{{ record.approval_status || (record.status ? 'Pending' : 'Draft') }}</v-chip>
        <v-btn class="d-print-none" icon="mdi-printer" variant="text" @click="printPage" />
      </v-card-title>
      <v-card-text class="pt-4">
        <v-row dense>
          <v-col cols="6" md="3"><Info label="Production Delivery" :value="record.production_delivery?.code || '-'" /></v-col>
          <v-col cols="6" md="3"><Info label="Production" :value="record.production_delivery?.production?.code || '-'" /></v-col>
          <v-col cols="6" md="3"><Info label="Receive Date" :value="dateOnly(record.receive_date)" /></v-col>
          <v-col cols="6" md="3"><Info label="Warehouse" :value="record.warehouse?.name || '-'" /></v-col>
          <v-col cols="6" md="3"><Info label="Receipt Value" :value="number(record.amount, 2)" /></v-col>
          <v-col cols="6" md="3"><Info label="Posted to Stock" :value="record.posted_at ? 'Yes' : 'No'" /></v-col>
          <v-col cols="12"><Info label="Remarks" :value="record.remarks || '-'" /></v-col>
        </v-row>

        <v-table density="compact" class="border-sm mt-4">
          <thead>
            <tr>
              <th>Finished Product</th>
              <th>Batch / Inventory Lot</th>
              <th>Manufactured</th>
              <th>Expiry</th>
              <th class="text-end">Qty</th>
              <th class="text-end">Material</th>
              <th class="text-end">Packaging</th>
              <th class="text-end">Labour</th>
              <th class="text-end">Overhead</th>
              <th class="text-end">Full Unit Cost</th>
              <th class="text-end">Amount</th>
            </tr>
          </thead>
          <tbody>
            <tr v-for="detail in record.details || []" :key="detail.id">
              <td>{{ detail.product?.code }} - {{ detail.product?.name }}</td>
              <td>{{ detail.batch_no || '-' }} / {{ detail.lot_no || '-' }}</td>
              <td>{{ dateOnly(detail.manufacturing_date) || '-' }}</td>
              <td>{{ dateOnly(detail.expiry_date) || '-' }}</td>
              <td class="text-end">{{ number(detail.qty) }}</td>
              <td class="text-end">{{ number(detail.material_unit_cost, 6) }}</td>
              <td class="text-end">{{ number(detail.packaging_unit_cost, 6) }}</td>
              <td class="text-end">{{ number(detail.labour_unit_cost, 6) }}</td>
              <td class="text-end">{{ number(detail.overhead_unit_cost, 6) }}</td>
              <td class="text-end font-weight-bold">{{ number(detail.rate, 6) }}</td>
              <td class="text-end">{{ number(detail.amount, 2) }}</td>
            </tr>
          </tbody>
        </v-table>

        <v-alert type="info" variant="tonal" density="compact" class="mt-4">
          Inventory stores purchase cost as Material + Packaging and other cost as Labour + Overhead, while the full production unit cost remains traceable on this receipt.
        </v-alert>
      </v-card-text>
    </v-card>
    <v-skeleton-loader v-else type="article, table" />
  </v-container>
</template>

<script setup>
import { defineComponent, h, onMounted, ref } from "vue";
import { useRoute } from "vue-router";
import { useStore } from "vuex";
import { toast } from "vue3-toastify";

const Info = defineComponent({
  props: { label: String, value: [String, Number] },
  setup(props) {
    return () => h("div", { class: "info-box" }, [
      h("div", { class: "info-label" }, props.label),
      h("div", { class: "font-weight-medium" }, props.value ?? "-"),
    ]);
  },
});

const route = useRoute();
const store = useStore();
const record = ref(null);

function number(value, digits = 4) {
  return Number(value || 0).toLocaleString(undefined, { maximumFractionDigits: digits });
}

function dateOnly(value) {
  return value ? String(value).slice(0, 10) : "";
}

function approvalColor(status) {
  if (status === "Approved") return "success";
  if (status === "Cancelled" || status === "Rejected") return "error";
  return "warning";
}

function printPage() {
  window.print();
}

onMounted(async () => {
  try {
    const response = await store.dispatch("getInvProductionReceiveById", route.params.id);
    record.value = response?.data || response;
  } catch (error) {
    toast.error(error?.response?.data?.message || "Finished-goods receipt details could not be loaded.");
  }
});
</script>

<style scoped>
.info-box { border: 1px solid rgba(0, 0, 0, 0.12); border-radius: 4px; height: 100%; padding: 8px 10px; }
.info-label { color: rgba(0, 0, 0, 0.6); font-size: 0.7rem; text-transform: uppercase; }
</style>
